// The minimap's camera view box: the 3D camera's ground footprint, clipped to the
// board so it can never leave the map.
//
// The game camera is pitched ~33° (height 8.5, back offset 13) with a 45° vertical
// FOV, so the top of the frame points ~9° below horizontal: the two far frustum
// corners meet the ground some 55 units from the camera, while the board is only
// ~28 units deep. The published footprint is therefore a long wedge that runs well
// past the map — and a wedge drawn across the whole minimap reads as a rendering
// bug rather than as "this is what the camera sees".
//
// Clipping the polygon to the board's bounds keeps the box on the map and turns it
// into the shape players expect: the wedge of board the camera can see, cut off at
// the map edge. Pure (no canvas), so it is unit-tested on its own.

import type { Bounds, Point } from './minimapGeometry.js';

/** A point on the segment a→b where it crosses the vertical line x = k. */
function crossVertical(a: Point, b: Point, k: number): Point {
  const dx = b.x - a.x;
  if (Math.abs(dx) < 1e-12) return { x: k, y: a.y };
  const t = (k - a.x) / dx;
  return { x: k, y: a.y + t * (b.y - a.y) };
}

/** A point on the segment a→b where it crosses the horizontal line y = k. */
function crossHorizontal(a: Point, b: Point, k: number): Point {
  const dy = b.y - a.y;
  if (Math.abs(dy) < 1e-12) return { x: a.x, y: k };
  const t = (k - a.y) / dy;
  return { x: a.x + t * (b.x - a.x), y: k };
}

/** Keep the part of the polygon on the `inside` side of one edge (Sutherland–Hodgman). */
function clipToEdge(
  poly: readonly Point[],
  inside: (p: Point) => boolean,
  cross: (a: Point, b: Point) => Point,
): Point[] {
  if (poly.length === 0) return [];
  const out: Point[] = [];
  for (let i = 0; i < poly.length; i++) {
    const cur = poly[i] as Point;
    const prev = poly[(i + poly.length - 1) % poly.length] as Point;
    const curIn = inside(cur);
    const prevIn = inside(prev);
    if (curIn) {
      if (!prevIn) out.push(cross(prev, cur));
      out.push(cur);
    } else if (prevIn) {
      out.push(cross(prev, cur));
    }
  }
  return out;
}

/**
 * Clip a polygon to an axis-aligned rectangle. Returns the part of the polygon
 * inside the rectangle (a new array; the input is not mutated), or an empty array
 * when the polygon lies entirely outside.
 */
/**
 * Area enclosed by a polygon (shoelace, absolute). Used to decide whether the
 * camera footprint is worth drawing at all: one that covers most of the board
 * carries no information and reads as stray lines across the minimap.
 */
export function polygonArea(poly: readonly Point[]): number {
  if (poly.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[(i + 1) % poly.length]!;
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

export function clipPolygonToBounds(poly: readonly Point[], bounds: Bounds): Point[] {
  let out: Point[] = poly.map((p) => ({ x: p.x, y: p.y }));
  out = clipToEdge(
    out,
    (p) => p.x >= bounds.minX,
    (a, b) => crossVertical(a, b, bounds.minX),
  );
  out = clipToEdge(
    out,
    (p) => p.x <= bounds.maxX,
    (a, b) => crossVertical(a, b, bounds.maxX),
  );
  out = clipToEdge(
    out,
    (p) => p.y >= bounds.minY,
    (a, b) => crossHorizontal(a, b, bounds.minY),
  );
  out = clipToEdge(
    out,
    (p) => p.y <= bounds.maxY,
    (a, b) => crossHorizontal(a, b, bounds.maxY),
  );
  return out;
}
